import { FC } from 'react';
import { Dropdown } from 'alurkerja-ui';
import { PanelLeft } from 'lucide-react';
import { TbBell } from "react-icons/tb";
import TenantList from './TenantList';
import Breadcrumb from './Breadcrumb';
import { env } from '@/utils/constants/env';
import { FaUserAlt } from 'react-icons/fa';
import { useUnifiedUserInfo, useUserAvatarUrl } from '@/utils/auth/userInfo';
import clsx from 'clsx';

export interface BellButtonProps {
  notificationCount?: number;
}

export interface HeaderProps extends BellButtonProps {
  showNotification?: boolean;
  onClickNotification?: () => void;
  onListenNotification?: (open: boolean) => void;
  avatarContent?: JSX.Element;
  notificationContent?: JSX.Element;
  toggled: boolean;
  setToggled: (toggled: boolean) => void;
  rightContent?: JSX.Element;
}

export const Header: FC<HeaderProps> = ({
  showNotification = true,
  onClickNotification,
  onListenNotification,
  avatarContent,
  notificationContent,
  notificationCount,
  toggled,
  setToggled,
  rightContent,
}) => {
  const userInfo = useUnifiedUserInfo();
  const avatarUrl = useUserAvatarUrl();
  const fullAvatarUrl = avatarUrl ? env.cloudStorage + avatarUrl : null;

  const buildNotification = (): JSX.Element => {
    if (!showNotification) {
      return <></>;
    }

    if (!onClickNotification) {
      return (
        <Dropdown
          triggerElement={<BellButton {...{ notificationCount }} />}
          content={notificationContent}
          onListen={onListenNotification}
        />
      );
    }

    return (
      <button onClick={onClickNotification} className="hover:cursor-pointer">
        <BellButton {...{ notificationCount }} />
      </button>
    );
  };

  // Keycloak-js handles token refresh automatically via onTokenExpired event handler
  // No manual token refresh needed here

  return (
    <header className="flex z-20 items-center justify-between w-full h-12 md:px-6 px-4 border-b bg-white">
      <div className='flex items-center h-full  gap-3 '>

        {/* Header */}
        <div className="flex items-center self-center font-bold tracking-wide ">
          {/* <div className="truncate"> */}
          <TenantList />
          {/* </div> */}
        </div>

        <button
          onClick={() => setToggled(!toggled)}
          className={clsx(
            ' top-2 transition-all duration-300 p-1 hover:bg-gray-100 md:hidden rounded-md',

          )}
        >
          <div className={clsx('transition-transform duration-300', toggled && 'rotate-180')}>
            <PanelLeft size={18} />
          </div>
        </button>

        <Breadcrumb className="md:absolute md:left-60" />

      </div>
      <div className="flex items-center justify-end gap-2">
        {rightContent}
        {buildNotification()}

        <Dropdown
          triggerElement={
            <div className="flex items-center justify-center gap-2">
              <div className="bg-gray-200 text-primary h-7 w-7 aspect-square cursor-pointer flex items-center justify-center rounded-full overflow-hidden">
                {fullAvatarUrl && (
                  <img src={fullAvatarUrl} alt="user's avatar" className="w-full h-full aspect-square" />
                )}
                {!fullAvatarUrl && <FaUserAlt />}
              </div>
              <div className="cursor-pointer select-none hidden md:block">
                <span className="select-none font-medium text-sm text-gray-500">{userInfo?.name}</span>
              </div>
            </div>
          }
          content={avatarContent}
        />
      </div>
    </header>
  );
};

const BellButton: FC<BellButtonProps> = ({ notificationCount }) => {
  const count = notificationCount ?? 0;
  let rightClass: string;
  if (count >= 100) {
    rightClass = '-right-4';
  } else if (count >= 10) {
    rightClass = '-right-2';
  } else {
    rightClass = 'right-0';
  }

  return (
    <div className="relative p-1 hover:cursor-pointer hover:bg-black/5 rounded-md">
      {count > 0 && (
        <div
          className={`absolute top-0 py-[0.2px] px-[4px] text-[10px] text-white bg-red-500 rounded-full ${rightClass}`}
        >
          {count >= 100 ? '99+' : count}
        </div>
      )}

      <TbBell size={18} className="-rotate-12" />
    </div>
  );
};

export default Header;
